/**
 * THRM-04 — vertical furnace and rapid thermal processing.
 *
 * Two processes, one shared and non-renewable resource.
 *
 * OXIDATION follows Deal-Grove: x^2 + Ax = B(t + tau). The linear term is the
 * interface reaction and the parabolic term is oxygen diffusing through oxide
 * already grown, so a thin oxide grows linearly and a thick one grows as the
 * square root of time. That crossover is why a gate oxide and a field oxide are
 * grown completely differently.
 *
 * DIFFUSION spreads implanted dopant by Fick's law, and the only thing that
 * matters is the product D*t. Which means every hot step in the flow spends
 * from the same account: an anneal that fixes implant damage also moves a
 * junction formed three steps earlier. That shared budget - "thermal budget" -
 * is the competency this module exists to teach, and it is the reason a process
 * integration engineer cares about steps that are not theirs.
 */

import { clamp } from '../../core/math'
import { projectedRangeNm } from '../implant/ImplantSim'

const K_EV = 8.617333262e-5 // Boltzmann constant [eV/K]

export type Ambient = 'dry' | 'wet'
export type Dopant = 'boron' | 'phosphorus' | 'arsenic'

/**
 * Deal-Grove rate constants, in the units the original paper uses:
 * B in um^2/hr, B/A in um/hr. These are the measured Arrhenius fits for
 * silicon <100>.
 */
const DEAL_GROVE = {
  dry: { b0: 7.72e2, bEa: 1.23, ba0: 6.23e6, baEa: 2.0 },
  wet: { b0: 3.86e2, bEa: 0.78, ba0: 1.63e8, baEa: 2.05 },
} as const

/** Dopant diffusivity prefactor [cm^2/s] and activation energy [eV] in silicon. */
const DIFFUSIVITY = {
  boron: { d0: 0.76, ea: 3.46, maxActive: 2.5e20 },
  phosphorus: { d0: 3.85, ea: 3.66, maxActive: 4e20 },
  arsenic: { d0: 22.9, ea: 4.1, maxActive: 2e20 },
} as const

export interface ThermalRecipe {
  /** Oxidation ambient. */
  ambient: Ambient
  /** Oxidation temperature [C]. */
  oxideTempC: number
  /** Oxidation time [min]. */
  oxideMinutes: number
  /** Dopant implanted before the thermal steps. */
  dopant: Dopant
  /** Implant dose [cm^-2]. */
  implantDose: number
  /** Implant energy [keV] - sets how deep the dopant starts. */
  implantKeV: number
  /** Anneal temperature [C]. */
  annealTempC: number
  /** Anneal time [s]. Rapid thermal anneals are seconds; furnace anneals minutes. */
  annealSeconds: number
}

export const DEFAULT_THERMAL_RECIPE: ThermalRecipe = {
  ambient: 'dry',
  oxideTempC: 900,
  oxideMinutes: 20,
  dopant: 'boron',
  implantDose: 3e15,
  implantKeV: 30,
  annealTempC: 1050,
  annealSeconds: 20,
}

export const THERMAL_LIMITS = {
  oxideTempC: { min: 700, max: 1200, step: 5, unit: '°C', label: 'Oxidation temperature' },
  oxideMinutes: { min: 1, max: 480, step: 1, unit: 'min', label: 'Oxidation time' },
  implantDose: { min: 1e13, max: 1e16, step: 1e13, unit: 'cm⁻²', label: 'Implant dose' },
  implantKeV: { min: 5, max: 200, step: 1, unit: 'keV', label: 'Implant energy' },
  annealTempC: { min: 600, max: 1200, step: 5, unit: '°C', label: 'Anneal temperature' },
  annealSeconds: { min: 1, max: 3600, step: 1, unit: 's', label: 'Anneal time' },
} as const

/** Deal-Grove oxide thickness [nm] grown on an existing oxide. */
export function growOxide(
  tempC: number,
  minutes: number,
  ambient: Ambient,
  initialNm = 2,
): number {
  const T = tempC + 273.15
  const c = DEAL_GROVE[ambient]
  const B = c.b0 * Math.exp(-c.bEa / (K_EV * T)) // um^2/hr
  const BA = c.ba0 * Math.exp(-c.baEa / (K_EV * T)) // um/hr
  const A = B / BA

  const xi = initialNm / 1000 // um
  // tau accounts for the oxide already present.
  const tau = (xi * xi + A * xi) / B
  const t = minutes / 60 + tau

  const x = (A / 2) * (Math.sqrt(1 + (4 * B * t) / (A * A)) - 1)
  return x * 1000
}

/**
 * Deal-Grove for an ambient that is neither dry nor wet, but a mixture.
 *
 * A "dry" oxidation with a weeping steam valve or a wet O2 line is not a rare
 * fault, and modelling it needs one physical fact: both Deal-Grove rate
 * constants are proportional to the oxidant solubility in the growing oxide,
 * and solubilities of independent species ADD. So B and B/A each blend linearly
 * in the water fraction, and nothing else about the model changes.
 *
 * That linearity is the reason this can be done honestly at all. Interpolating
 * the resulting THICKNESS between a dry and a wet run would have been wrong -
 * the thickness is a square root of the constants, not a linear function of
 * them - and it would have understated the fault everywhere it matters.
 *
 * @param moisture Fraction of the oxidant that is water vapour, 0 to 1.
 */
export function growOxideMixed(
  tempC: number,
  minutes: number,
  moisture: number,
  initialNm = 2,
): number {
  const f = Math.min(Math.max(moisture, 0), 1)
  if (f === 0) return growOxide(tempC, minutes, 'dry', initialNm)
  if (f === 1) return growOxide(tempC, minutes, 'wet', initialNm)

  const T = tempC + 273.15
  const rate = (c: { b0: number; bEa: number; ba0: number; baEa: number }) => ({
    B: c.b0 * Math.exp(-c.bEa / (K_EV * T)),
    BA: c.ba0 * Math.exp(-c.baEa / (K_EV * T)),
  })
  const d = rate(DEAL_GROVE.dry)
  const w = rate(DEAL_GROVE.wet)

  const B = (1 - f) * d.B + f * w.B
  const BA = (1 - f) * d.BA + f * w.BA
  const A = B / BA

  const xi = initialNm / 1000
  const tau = (xi * xi + A * xi) / B
  const t = minutes / 60 + tau
  return (A / 2) * (Math.sqrt(1 + (4 * B * t) / (A * A)) - 1) * 1000
}

/** Which Deal-Grove term dominates: reaction-limited or diffusion-limited. */
export function oxidationRegime(
  tempC: number,
  thicknessNm: number,
  ambient: Ambient,
): { regime: 'linear' | 'mixed' | 'parabolic'; ratio: number } {
  const T = tempC + 273.15
  const c = DEAL_GROVE[ambient]
  const B = c.b0 * Math.exp(-c.bEa / (K_EV * T))
  const BA = c.ba0 * Math.exp(-c.baEa / (K_EV * T))
  const A = B / BA
  // x/A compares the grown thickness with the crossover thickness.
  const ratio = thicknessNm / 1000 / A
  return {
    regime: ratio < 0.3 ? 'linear' : ratio < 3 ? 'mixed' : 'parabolic',
    ratio,
  }
}

/** Dopant diffusivity [cm^2/s] at a temperature. */
export function diffusivity(dopant: Dopant, tempC: number): number {
  const d = DIFFUSIVITY[dopant]
  return d.d0 * Math.exp(-d.ea / (K_EV * (tempC + 273.15)))
}

/**
 * Where the dopant starts, before this furnace moves it.
 *
 * The range itself is IMP-10's number, not this tool's. The furnace does
 * not get its own opinion about where the implanter put the dopant - if the
 * two ever disagreed, a junction depth would depend on which panel you asked,
 * and the coupling this module teaches would be a coincidence rather than a
 * consequence. Straggle is about 38% of range for the LSS profiles.
 */
export function implantProfile(dopant: Dopant, keV: number): { rpNm: number; straggleNm: number } {
  const rpNm = projectedRangeNm(dopant, keV)
  return { rpNm, straggleNm: rpNm * 0.38 }
}

export interface ThermalStep {
  label: string
  tempC: number
  seconds: number
}

export interface DopantProfile {
  /** Depth samples [nm]. */
  depthNm: Float64Array
  /** Concentration [cm^-3]. */
  concentration: Float64Array
  /** Junction depth where the profile meets the substrate doping [nm]. */
  junctionNm: number
  /** Peak concentration [cm^-3]. */
  peak: number
  /** Sheet resistance [ohm/square]. */
  sheetResistance: number
  /** Fraction of the implanted dose that is electrically active. */
  activeFraction: number
  /** Accumulated Dt across every thermal step [cm^2]. */
  dtTotal: number
}

/** Substrate background doping [cm^-3]. */
const SUBSTRATE = 1e15

/**
 * Carrier mobility against doping - the Caughey-Thomas fit. Needed because
 * sheet resistance is not just about how much dopant is there: heavily doped
 * silicon scatters its own carriers, so doubling the dose does not halve the
 * resistance.
 */
function mobility(dopant: Dopant, n: number): number {
  const isP = dopant === 'boron'
  const muMin = isP ? 44.9 : 68.5
  const muMax = isP ? 470.5 : 1414
  const nRef = isP ? 2.23e17 : 9.2e16
  const alpha = isP ? 0.719 : 0.711
  return muMin + (muMax - muMin) / (1 + Math.pow(n / nRef, alpha))
}

/**
 * Diffuse an implant through a sequence of thermal steps.
 *
 * Every step contributes D*t, and the profile only ever sees the SUM. That is
 * the whole lesson: the furnace does not know or care which step was "for" the
 * junction, and neither does the silicon.
 */
export function diffuse(
  recipe: ThermalRecipe,
  extraSteps: ThermalStep[] = [],
  samples = 300,
): DopantProfile {
  const { rpNm, straggleNm } = implantProfile(recipe.dopant, recipe.implantKeV)

  const steps: ThermalStep[] = [
    // Oxidation is a thermal step too, whether or not anyone counted it.
    { label: 'oxidation', tempC: recipe.oxideTempC, seconds: recipe.oxideMinutes * 60 },
    { label: 'anneal', tempC: recipe.annealTempC, seconds: recipe.annealSeconds },
    ...extraSteps,
  ]

  let dtTotal = 0
  for (const s of steps) dtTotal += diffusivity(recipe.dopant, s.tempC) * s.seconds

  // Convert cm^2 to nm^2 for profile arithmetic.
  const dtNm2 = dtTotal * 1e14
  // Implant straggle and diffusion add in quadrature.
  const sigma2 = straggleNm * straggleNm + 2 * dtNm2
  const sigma = Math.sqrt(sigma2)

  // Activation: dopant must sit on a lattice site to conduct, and that needs
  // heat. It also cannot exceed solid solubility however much you implant.
  const anneal = recipe.annealTempC + 273.15
  // Prefactor calibrated so a 30 s anneal at 1050 C activates essentially all
  // of the implant, and 700 C activates almost none - which is the measured
  // behaviour and the reason implant anneals are hot and short rather than warm
  // and long.
  const ACTIVATION_PREFACTOR = 2e11
  const activationRate =
    1 - Math.exp(-recipe.annealSeconds * ACTIVATION_PREFACTOR * Math.exp(-3.2 / (K_EV * anneal)))
  const solubility = DIFFUSIVITY[recipe.dopant].maxActive

  const depthNm = new Float64Array(samples)
  const concentration = new Float64Array(samples)
  const maxDepth = Math.max(rpNm + 6 * sigma, 200)

  // Gaussian, normalised so the integral is the implanted dose.
  const doseNm2 = recipe.implantDose * 1e-14 // cm^-2 -> nm^-2
  const prefactor = doseNm2 / (Math.sqrt(2 * Math.PI) * sigma)

  let peak = 0
  for (let i = 0; i < samples; i++) {
    const z = (i / (samples - 1)) * maxDepth
    depthNm[i] = z
    const gauss = prefactor * Math.exp(-((z - rpNm) ** 2) / (2 * sigma2))
    // nm^-3 -> cm^-3
    const c = gauss * 1e21
    concentration[i] = c
    if (c > peak) peak = c
  }

  // Junction where the profile crosses the substrate background.
  let junctionNm = 0
  for (let i = 0; i < samples; i++) {
    if (concentration[i] > SUBSTRATE) junctionNm = depthNm[i]
  }

  // Sheet resistance from the active part of the profile.
  const dz = maxDepth / (samples - 1)
  let conductance = 0
  for (let i = 0; i < samples; i++) {
    const active = Math.min(concentration[i], solubility) * activationRate
    if (active <= SUBSTRATE) continue
    // q * mu * n * dz, with dz in cm.
    conductance += 1.602176634e-19 * mobility(recipe.dopant, active) * active * dz * 1e-7
  }
  const sheetResistance = conductance > 0 ? 1 / conductance : Infinity

  return {
    depthNm,
    concentration,
    junctionNm,
    peak,
    sheetResistance,
    activeFraction: clamp(activationRate, 0, 1),
    dtTotal,
  }
}

export interface ThermalResult {
  oxideNm: number
  oxideRegime: ReturnType<typeof oxidationRegime>
  profile: DopantProfile
  /** Thermal budget expressed as an equivalent time at 1000 C [s]. */
  budgetEquivalentSeconds: number
}

/**
 * Run the whole thermal sequence.
 *
 * The budget is reported as "how many seconds at 1000 C would have done the
 * same amount of diffusion", because a number in cm^2 means nothing to anyone
 * and an equivalent time is immediately comparable between steps.
 */
export function runThermal(recipe: ThermalRecipe, extraSteps: ThermalStep[] = []): ThermalResult {
  const oxideNm = growOxide(recipe.oxideTempC, recipe.oxideMinutes, recipe.ambient)
  const profile = diffuse(recipe, extraSteps)
  const dRef = diffusivity(recipe.dopant, 1000)
  return {
    oxideNm,
    oxideRegime: oxidationRegime(recipe.oxideTempC, oxideNm, recipe.ambient),
    profile,
    budgetEquivalentSeconds: dRef > 0 ? profile.dtTotal / dRef : 0,
  }
}

export const THERMAL_PRESETS: Record<
  string,
  { label: string; note: string; recipe: ThermalRecipe }
> = {
  thermalBaseline: {
    label: 'GATE OXIDE + RTA',
    note: 'Dry gate oxide at 1000 °C, then a 30 s rapid anneal to activate a shallow boron implant.',
    recipe: { ...DEFAULT_THERMAL_RECIPE },
  },
  thermalWetField: {
    label: 'WET FIELD OXIDE',
    note: 'Steam at 1050 °C for two hours. Far thicker, far faster — and it spends a lot of budget.',
    recipe: { ...DEFAULT_THERMAL_RECIPE, ambient: 'wet', oxideTempC: 1050, oxideMinutes: 120 },
  },
  thermalFurnaceAnneal: {
    label: 'FURNACE ANNEAL',
    note: 'Same implant, but annealed 30 minutes in a furnace instead of 30 seconds in an RTP.',
    recipe: { ...DEFAULT_THERMAL_RECIPE, annealSeconds: 1800 },
  },
  thermalShallowJunction: {
    label: 'SHALLOW JUNCTION',
    note: 'Low-energy implant with a short, hot spike anneal — the modern recipe for a shallow junction.',
    recipe: {
      ...DEFAULT_THERMAL_RECIPE,
      implantKeV: 8,
      annealTempC: 1100,
      annealSeconds: 2,
      oxideMinutes: 8,
    },
  },
  thermalArsenicSd: {
    label: 'ARSENIC SOURCE/DRAIN',
    note: 'Arsenic instead of boron. A far heavier atom, so it barely moves — which is the point.',
    recipe: { ...DEFAULT_THERMAL_RECIPE, dopant: 'arsenic', implantKeV: 40, implantDose: 5e15 },
  },
}
